import React, { useState, useEffect, useRef } from 'react';
import { authFetch, API } from './api';
import { track as trackStep, setTrackContext, flushTracking, trackLang } from './pp/track';
import { packingText } from './packingText';
import { useDelta, SpeedChip, SpeedSheet, speedCls, deltaCls, deltaArrow, fmtDelta } from './pp/SpeedChip';
import { fmtDur } from './statistics/shared';

// ── The picking walk ──────────────────────────────────────────────────────────
//
// Fernando (2026-09-15) reworked this screen for the Android tablet on the cart:
//   * soft grey background, black text, black arrows — the only colour is a red
//     "!" when a row needs more than one piece (eight-hour days, calm screen);
//   * one big row per item, centred, with ← on its left when the shelf is on the
//     picker's left and → on its right when it is on the right — the two "Your
//     Left / Your Right" columns are gone;
//   * the shop's stock count in brackets behind the picklist number;
//   * tap a row to see name, article number, restock location(s), barcode;
//   * sizes smallest → biggest, top → bottom (the plan now carries size_sort);
//   * a Fullscreen button where the browser supports it (Chrome; Safari on iPad
//     simply does not show it — nothing else differs).
// Colours are arbitrary hex classes on purpose: the light-theme sheet and the
// accent variable in index.css remap the stock utility classes, and this screen
// must look the same on every tablet regardless of the user's theme.

// ── Types ─────────────────────────────────────────────────────────────────────

interface PickItem {
  pick_number: number;
  order_id: string;
  product_code: string;
  picklist: string;
  quantity: number;
  location: string;
  // Added 2026-09-15 — /api/picking/plan carries the warehouse facts per line.
  line_id?: string;
  name?: string;
  article_number?: string;
  barcode?: string;
  size_sort?: number | null;
  stock_qty?: number | null;
  stock_infinite?: boolean;
  restock_location?: string | null;
  // 2026-10-08: the shelf is under inventory count — leave the piece, inventory brings it
  under_count?: boolean;
  under_count_by?: string | null;
  // 2026-10-26: the line waits for a NOS supplier delivery (WGR) — shown in "Just look" only
  waiting_for_nos?: boolean;
}

interface PlanStop {
  stop_number: number;
  picker_left_locations: string[];
  picker_right_locations: string[];
  picker_left_items: PickItem[];
  picker_right_items: PickItem[];
  has_left: boolean;
  has_right: boolean;
}

interface PlanAisle {
  aisle_name: string;
  box_label: string;
  entry_from: 'near' | 'far';
  stops: PlanStop[];
}

interface PlanLoad {
  load_number: number;
  boxes: string[];
  aisles: PlanAisle[];
}

interface PickingPlan {
  mode: 'single_trip' | 'rounds';
  order_count: number;
  skip_intro: boolean;
  loads: PlanLoad[];
  unlocated_items: PickItem[];
  /** The cart the trips were sized for (chosen on the tablet), or null. */
  cart?: { id: string; name: string; boxes: number } | null;
}

interface FlatStop extends PlanStop {
  aisle_name: string;
  box_label: string;
}

type Side = 'left' | 'right' | 'none';

/** One line on the screen: the same shape on the list, the stop and the
 *  unlocated screen. Duplicate picklist+location pairs are merged into one. */
interface PickRow {
  key: string;
  picklist: string;
  product_code: string;
  location: string;
  side: Side;
  quantity: number;
  is_located: boolean;
  size_sort: number | null;
  name: string;
  article_number: string;
  barcode: string;
  stock_qty: number | null;
  stock_infinite: boolean;
  restock_location: string;
  under_count: boolean;
  under_count_by: string;
  /** pieces of this row waiting for a NOS supplier delivery (summed like quantity when rows merge) */
  nos_waiting: number;
}

// ── Palette (see header comment) ──────────────────────────────────────────────

/** taps closer than this to the last stop change are ignored (a double tap skipped a stop, 19.10) */
const STEP_GUARD_MS = 800;

const C = {
  bg:        'bg-[#e5e7eb]',
  panel:     'bg-[#f3f4f6] border-[#c8ccd2]',
  header:    'bg-[#d9dce1] border-[#c8ccd2]',
  text:      'text-[#111111]',
  muted:     'text-[#5f6368]',
  faint:     'text-[#8a8f96]',
  primary:   'bg-[#111111] text-[#ffffff] active:bg-[#333333]',
  secondary: 'bg-[#ffffff] border border-[#9aa0a6] text-[#111111] active:bg-[#e5e7eb]',
  chip:      'bg-[#ffffff] border border-[#9aa0a6] text-[#111111]',
  bar:       'bg-[#c8ccd2]',
  barFill:   'bg-[#111111]',
  alert:     'text-red-600',
};

// ── Size helpers (fallback only — the plan carries size_sort) ─────────────────

const SIZE_RANK: Record<string, number> = {
  'XXS': 1, 'XS': 2, 'S': 3, 'M': 4, 'L': 5, 'XL': 6, 'XXL': 7, '2XL': 7,
  '3XL': 8, '4XL': 9, '5XL': 10, '6XL': 11, '7XL': 12, '8XL': 13, '9XL': 14, '10XL': 15,
};

function extractSize(name: string): string {
  const m = name.match(/[-\s](\d{0,2}XL|XXL|XXS|XS|XL|[LMSX])$/i);
  return m ? m[1].toUpperCase() : '';
}

function extractStyle(name: string): string {
  const dashIdx = name.lastIndexOf(' - ');
  if (dashIdx > 0) return name.slice(0, dashIdx).trim();
  return name.replace(/[-\s](\d{0,2}XL|XXL|XXS|XS|XL|[LMSX])$/i, '').trim() || name;
}

function sizeKey(row: { size_sort: number | null; picklist: string }): number {
  if (row.size_sort != null) return row.size_sort;
  const r = SIZE_RANK[extractSize(row.picklist)];
  return r ?? Number.POSITIVE_INFINITY;          // unknown size sorts last
}

// ── Rows ──────────────────────────────────────────────────────────────────────

function rowFrom(item: PickItem, side: Side, located: boolean): PickRow {
  const picklist = item.picklist || item.product_code;
  return {
    key: `${picklist}||${located ? item.location : '__none__'}`,
    picklist,
    product_code: item.product_code,
    location: item.location || '',
    side,
    quantity: item.quantity,
    is_located: located,
    size_sort: item.size_sort ?? null,
    name: item.name || '',
    article_number: item.article_number || '',
    barcode: item.barcode || '',
    stock_qty: item.stock_qty ?? null,
    stock_infinite: !!item.stock_infinite,
    restock_location: item.restock_location || '',
    under_count: !!item.under_count,
    under_count_by: item.under_count_by || '',
    nos_waiting: item.waiting_for_nos ? item.quantity : 0,
  };
}

/** Merge duplicates (same picklist at the same shelf) and sum their pieces. */
function mergeRows(items: { item: PickItem; side: Side; located: boolean }[]): PickRow[] {
  const map = new Map<string, PickRow>();
  for (const { item, side, located } of items) {
    const r = rowFrom(item, side, located);
    const have = map.get(r.key);
    if (have) { have.quantity += r.quantity; have.nos_waiting += r.nos_waiting;
                have.under_count = have.under_count || r.under_count; have.under_count_by = have.under_count_by || r.under_count_by; }
    else map.set(r.key, r);
  }
  return Array.from(map.values());
}

/** The one sort rule (Fernando, 2026-09-15): shelf in walking order, then style,
 *  then size smallest → biggest. `locOrder` is the walk — the index of a shelf in
 *  it is its rank; shelves not in it go last, in text order. */
function sortRows(rows: PickRow[], locOrder: string[]): PickRow[] {
  const rank = new Map<string, number>();
  locOrder.forEach((loc, i) => { if (!rank.has(loc)) rank.set(loc, i); });
  const locRank = (loc: string) => rank.get(loc) ?? Number.POSITIVE_INFINITY;
  return [...rows].sort((a, b) => {
    const la = locRank(a.location), lb = locRank(b.location);
    if (la !== lb) return la - lb;
    if (a.location !== b.location) return a.location.localeCompare(b.location);
    const sa = extractStyle(a.picklist), sb = extractStyle(b.picklist);
    if (sa !== sb) return sa.localeCompare(sb);
    const za = sizeKey(a), zb = sizeKey(b);
    if (za !== zb) return za - zb;
    return a.picklist.localeCompare(b.picklist);
  });
}

/** Every stop's shelves in the order the picker meets them. */
function walkOrder(plan: PickingPlan): string[] {
  const out: string[] = [];
  for (const load of plan.loads)
    for (const aisle of load.aisles)
      for (const stop of aisle.stops)
        out.push(...stop.picker_left_locations, ...stop.picker_right_locations);
  return out;
}

function buildSummaryRows(plan: PickingPlan): PickRow[] {
  const located: { item: PickItem; side: Side; located: boolean }[] = [];
  for (const load of plan.loads)
    for (const aisle of load.aisles)
      for (const stop of aisle.stops) {
        for (const item of stop.picker_left_items)  located.push({ item, side: 'left',  located: true });
        for (const item of stop.picker_right_items) located.push({ item, side: 'right', located: true });
      }
  const unlocated = plan.unlocated_items.map(item => ({ item, side: 'none' as Side, located: false }));
  return [
    ...sortRows(mergeRows(located), walkOrder(plan)),
    ...sortRows(mergeRows(unlocated), []),
  ];
}

function stopRows(stop: PlanStop): PickRow[] {
  const items = [
    ...stop.picker_left_items.map(item  => ({ item, side: 'left'  as Side, located: true })),
    ...stop.picker_right_items.map(item => ({ item, side: 'right' as Side, located: true })),
  ];
  return sortRows(mergeRows(items), [...stop.picker_left_locations, ...stop.picker_right_locations]);
}

function flattenLoad(load: PlanLoad): FlatStop[] {
  const out: FlatStop[] = [];
  for (const aisle of load.aisles)
    for (const stop of aisle.stops)
      out.push({ ...stop, aisle_name: aisle.aisle_name, box_label: aisle.box_label });
  return out;
}

// ── Row component ─────────────────────────────────────────────────────────────

function stockText(row: PickRow, inStock: (n: number) => string): string {
  if (row.stock_infinite) return ' (NOS)';
  if (row.stock_qty == null) return '';
  return ` (${inStock(row.stock_qty)})`;
}

// Guided rounds (2026-10-17): "Can't find" on a row's details. The guided wrapper below provides the action; the old
// walk provides nothing and the button is not there.
const CantFindCtx = React.createContext<((row: PickRow) => void) | null>(null);
// "Just look" (Fernando, 26.10): the lines waiting for a NOS supplier delivery carry the red "Waiting for NOS" pill —
// on the look-only list, never on a walk that picks.
const JustLookCtx = React.createContext(false);

function PickRowView({ row, open, onToggle }: { row: PickRow; open: boolean; onToggle: () => void }) {
  const cantFind = React.useContext(CantFindCtx);
  const nos = React.useContext(JustLookCtx) && row.nos_waiting > 0;
  const multi = row.quantity > 1;
  const ptx = packingText(trackLang());
  return (
    <div className={`border-b border-[#c8ccd2] last:border-0 ${row.is_located ? '' : 'opacity-70'}`}>
      <button onClick={onToggle}
        className={`w-full flex items-center gap-3 sm:gap-4 px-3 sm:px-5 min-h-[72px] py-3 text-left select-none ${C.text} ${open ? 'bg-[#e9ebee]' : row.under_count ? 'bg-[#fef3c7]' : 'active:bg-[#e9ebee]'}`}>
        {/* The shelf label sits beside the arrow, on the shelf's side — "← E 14 … "
            for the left, "… J 63 →" for the right — so a row reads like the stop
            header (Fernando, 2026-09-15). Both slots are always rendered at a fixed
            width, so quantity and picklist line up on every row. */}
        <span className="w-9 text-4xl font-black leading-none text-center flex-shrink-0" aria-hidden>
          {row.side === 'left' ? '←' : ''}
        </span>
        <span className="font-mono font-bold text-2xl w-[5.5rem] flex-shrink-0">
          {row.side === 'right' ? '' : (row.location || '—')}
        </span>
        {/* the unit under the number (Fernando 21.10, teaching Shaw: "quantity should be mentioned"); amber when 2+ */}
        <span className={`w-12 text-right flex-shrink-0 leading-none ${multi ? 'text-[#b45309]' : ''}`}>
          <span className="block text-4xl font-black">{row.quantity}</span>
          <span className="block text-xs font-bold uppercase tracking-wide mt-1">{ptx.pcs}</span>
        </span>
        <span className={`w-6 text-4xl font-black leading-none flex-shrink-0 ${C.alert}`} aria-label={multi ? 'more than one piece' : undefined}>
          {multi ? '!' : ''}
        </span>
        <span className="flex-1 text-xl sm:text-2xl font-semibold leading-tight min-w-0">
          {row.picklist}
          <span className={`font-normal ${C.muted}`}>{stockText(row, ptx.inStock)}</span>
          {row.under_count && <span className="block text-base font-bold text-[#92400e]">⏸ {ptx.inventoryCounting}{row.under_count_by ? ` (${row.under_count_by})` : ''}</span>}
          {nos && (
            <span className="block mt-1">
              <span className="inline-block text-sm font-bold px-2 py-0.5 rounded bg-red-700 text-white">
                {ptx.nosWaiting(row.nos_waiting, row.quantity)}</span>
            </span>
          )}
        </span>
        <span className="font-mono font-bold text-2xl w-[5.5rem] text-right flex-shrink-0">
          {row.side === 'right' ? row.location : ''}
        </span>
        <span className="w-9 text-4xl font-black leading-none text-center flex-shrink-0" aria-hidden>
          {row.side === 'right' ? '→' : ''}
        </span>
      </button>
      {open && (
        <div className={`px-6 sm:px-20 pb-4 pt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-base ${C.text}`}>
          <Detail label="Name" value={row.name || '—'} />
          <Detail label="Article number" value={row.article_number || row.product_code || '—'} mono />
          <Detail label="Restock location(s)"
                  value={row.restock_location || 'none set — add it under Warehouse → Products'}
                  mono={!!row.restock_location} faint={!row.restock_location} />
          <Detail label="Barcode" value={row.barcode || '—'} mono />
          {row.under_count && <Detail label="Inventory" value={ptx.inventoryCountingLong} />}
          {nos && <Detail label="Waiting for NOS" value={ptx.nosWaitingLong(row.nos_waiting, row.quantity)} />}
          {!row.is_located && <Detail label="Shelf" value="not in the warehouse plan — pick manually" faint />}
          {cantFind && (
            <div className="sm:col-span-2 pt-2">
              <button onClick={e => { e.stopPropagation(); cantFind(row); }}
                className="px-5 py-3 rounded-xl bg-[#b91c1c] text-white text-lg font-bold active:bg-[#991b1b]">
                {"Can't find / damaged"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Detail({ label, value, mono, faint }: { label: string; value: string; mono?: boolean; faint?: boolean }) {
  return (
    <div>
      <div className={`text-xs uppercase tracking-wider ${C.muted}`}>{label}</div>
      <div className={`${mono ? 'font-mono' : ''} ${faint ? C.faint : ''} text-lg leading-snug break-words`}>{value}</div>
    </div>
  );
}

function RowList({ rows, openKey, onToggle, dividerBefore }:
  { rows: PickRow[]; openKey: string | null; onToggle: (k: string) => void; dividerBefore?: (row: PickRow, prev: PickRow | null) => string | null }) {
  const ptx = packingText(trackLang());
  return (
    <div className={`rounded-2xl border ${C.panel} overflow-hidden`}>
      {/* the headings (Fernando 21.10: "not clear what is what … like headlines") — the rows' own column widths */}
      <div className={`flex items-center gap-3 sm:gap-4 px-3 sm:px-5 py-1.5 border-b border-[#c8ccd2] bg-[#e5e7eb] text-xs font-bold uppercase tracking-wider ${C.muted}`}>
        <span className="w-9 flex-shrink-0" />
        <span className="w-[5.5rem] flex-shrink-0">{ptx.colShelf}</span>
        <span className="w-12 flex-shrink-0 text-right">{ptx.colQty}</span>
        <span className="w-6 flex-shrink-0" />
        <span className="flex-1 min-w-0">{ptx.colItem}</span>
        <span className="w-[5.5rem] flex-shrink-0 text-right">{ptx.colShelf}</span>
        <span className="w-9 flex-shrink-0" />
      </div>
      {rows.map((row, i) => {
        const divider = dividerBefore ? dividerBefore(row, i > 0 ? rows[i - 1] : null) : null;
        return (
          <React.Fragment key={row.key}>
            {divider && (
              <div className={`px-5 py-2 border-b border-[#c8ccd2] bg-[#e5e7eb] text-xs uppercase tracking-wider ${C.muted}`}>
                {divider}
              </div>
            )}
            <PickRowView row={row} open={openKey === row.key} onToggle={() => onToggle(row.key)} />
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Shared chrome ─────────────────────────────────────────────────────────────

function Btn({ kind = 'secondary', className = '', ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement> & { kind?: 'primary' | 'secondary' }) {
  return <button {...rest} className={`rounded-2xl font-bold transition-colors ${kind === 'primary' ? C.primary : C.secondary} ${className}`} />;
}

function BoxTiles({ boxes }: { boxes: string[] }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex gap-4 justify-center flex-wrap">
        {boxes.map(box => (
          <div key={box} className="bg-[#ffffff] border-2 border-[#111111] rounded-2xl px-10 py-8 text-center min-w-[9rem]">
            <div className={`text-6xl font-black ${C.text}`}>{box}</div>
          </div>
        ))}
      </div>
      <div className={`text-sm ${C.muted}`}>Letters read in walking order — place the box on the cart the same way.</div>
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  orderIds: string[];
  onClose: () => void;
  /** From the picking gate (pp/WhoIsPicking): the round being walked and the
   *  person walking it. Every step below is recorded against them. */
  roundId?: number | null;
  picker?: string | null;
  /** The cart chosen before the walk (pp/WhichCart); sizes the trips. */
  cartId?: string | null;
  /** "Just look" (2026-10-05): the same list, NOTHING recorded — no steps, no plan stats, no picker */
  viewOnly?: boolean;
  /** Guided P&P (pp2/CartApp, 2026-10-17): the plan the SERVER built and froze for this round + cart (nothing is
   *  fetched from a browser-held list) and where the picker stood, so a reload lands on the same stop. */
  presetPlan?: PickingPlan;
  startAt?: { phase: string; load: number; stop: number } | null;
  /** every change of phase / trip / stop, so the server always knows the position */
  onStep?: (p: { phase: string; load: number; stop: number }) => void;
  /** a break in the middle of the round (the round waits; the PIN opens it again) */
  onPause?: () => void;
}

// ── Main component ────────────────────────────────────────────────────────────

/** Guided rounds: a piece is not on the shelf (or is damaged). The server decides which order goes short and
 *  answers a sentence for the picker. */
export type CantFindReport = (p: { location: string; product_code: string; qty: number; picklist: string }) => Promise<string>;

export default function PickingWindow(props: Props & { onCantFind?: CantFindReport }) {
  const { onCantFind, ...rest } = props;
  const [cf, setCf] = useState<PickRow | null>(null);
  const [cfMsg, setCfMsg] = useState('');
  const [cfBusy, setCfBusy] = useState(false);
  const report = async (row: PickRow, qty: number) => {
    if (!onCantFind) return;
    setCfBusy(true);
    try {
      setCfMsg(await onCantFind({ location: row.location, product_code: row.product_code, qty, picklist: row.picklist }));
    } catch (e: any) {
      setCfMsg(e?.message || 'Could not report it — try again');
    } finally {
      setCfBusy(false); setCf(null);
    }
  };
  const inner = <JustLookCtx.Provider value={!!rest.viewOnly}><PickingWindowInner {...rest} /></JustLookCtx.Provider>;
  if (!onCantFind) return inner;
  return (
    <CantFindCtx.Provider value={row => { setCfMsg(''); setCf(row); }}>
      {inner}
      {cf && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => setCf(null)}>
          <div className={`rounded-2xl ${C.panel} border p-6 max-w-md w-full space-y-4`} onClick={e => e.stopPropagation()}>
            <div className={`text-2xl font-bold ${C.text}`}>{cf.picklist} · {cf.location}</div>
            <div className={`text-lg ${C.text}`}>How many are missing?</div>
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: Math.max(1, cf.quantity) }, (_, i) => i + 1).map(n => (
                <button key={n} disabled={cfBusy} onClick={() => report(cf, n)}
                  className="w-20 h-16 rounded-xl bg-[#b91c1c] text-white text-2xl font-bold disabled:opacity-40">{n}</button>
              ))}
            </div>
            <button onClick={() => setCf(null)} className={`px-5 py-3 rounded-xl ${C.secondary} text-lg`}>Cancel</button>
          </div>
        </div>
      )}
      {cfMsg && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] max-w-xl w-[92%] rounded-xl bg-[#111111] text-white px-5 py-4 text-lg shadow-lg"
             onClick={() => setCfMsg('')}>{cfMsg}</div>
      )}
    </CantFindCtx.Provider>
  );
}

function PickingWindowInner({ orderIds, onClose, roundId, picker, cartId, viewOnly = false,
                              presetPlan, startAt, onStep, onPause }: Props) {
  // every step below goes through `track`; looking records none of them
  const track: typeof trackStep = viewOnly ? () => {} : trackStep;
  const [plan,      setPlan]      = useState<PickingPlan | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [phase,     setPhase]     = useState<'summary' | 'intro' | 'picking' | 'box_change' | 'unlocated' | 'done'>('summary');
  const [loadIdx,   setLoadIdx]   = useState(0);
  const [stopIdx,   setStopIdx]   = useState(0);
  const [flatStops, setFlatStops] = useState<FlatStop[]>([]);
  const [savedPos,  setSavedPos]  = useState<{ loadIdx: number; stopIdx: number } | null>(null);
  // Which row's details are open — one at a time, keyed by picklist+shelf.
  const [openKey,   setOpenKey]   = useState<string | null>(null);

  // Fullscreen: only offered where the browser has the API (Chrome on the
  // Android tablet). Safari on iPad returns false here and shows no button.
  const fsSupported = typeof document !== 'undefined' && !!(document as any).fullscreenEnabled;
  const [isFs, setIsFs] = useState<boolean>(() => typeof document !== 'undefined' && !!document.fullscreenElement);
  useEffect(() => {
    const onChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const exitFullscreen = () => { if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); };
  const toggleFullscreen = () => {
    btn(isFs ? 'fullscreen_exit' : 'fullscreen_enter');
    if (document.fullscreenElement) exitFullscreen();
    else document.documentElement.requestFullscreen().catch(() => {});
  };

  // Guided: the server's frozen plan, applied ONCE with the stored position: plan, trip, stop and phase together
  // (round #210: a refetch kept the trip number and showed trip 1's shelves). The server wrote the plan stats.
  const presetDone = React.useRef(false);
  useEffect(() => {
    if (!presetPlan || presetDone.current) return;
    presetDone.current = true;
    setTrackContext({ roundId: roundId ?? null, actor: picker ?? null });
    const li = Math.min(Math.max(startAt?.load || 0, 0), Math.max(presetPlan.loads.length - 1, 0));
    const flat = presetPlan.loads.length ? flattenLoad(presetPlan.loads[li]) : [];
    setPlan(presetPlan);
    setLoadIdx(li);
    setFlatStops(flat);
    setStopIdx(Math.min(Math.max(startAt?.stop || 0, 0), Math.max(flat.length - 1, 0)));
    const ph = startAt?.phase || '';
    setPhase((['summary', 'intro', 'picking', 'box_change', 'unlocated', 'done'].includes(ph) ? ph : 'summary') as any);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetPlan]);
  useEffect(() => {
    if (!presetPlan || !plan) return;
    onStep?.({ phase, load: loadIdx, stop: stopIdx });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, loadIdx, stopIdx, plan]);

  useEffect(() => {
    if (presetPlan) return;          // guided: the plan above, never a fetch from a browser-held list
    if (!viewOnly) setTrackContext({ roundId: roundId ?? null, actor: picker ?? null });
    authFetch(`${API}/picking/plan`, {
      method: 'POST',
      body: JSON.stringify({ order_ids: orderIds, cart_id: cartId || undefined }),
    })
      .then(r => r.json())
      .then((data: PickingPlan) => {
        setPlan(data);
        if (data.loads.length > 0) setFlatStops(flattenLoad(data.loads[0]));
        setLoading(false);
        // Step 1 of the walk: the full list is on the screen.
        const stops = data.loads.reduce((s, l) => s + flattenLoad(l).length, 0);
        // the stops' first codes in walking order: the floor plan turns them into metres
        const codes = data.loads.flatMap(l => flattenLoad(l).map(s => s.picker_left_locations[0] || s.picker_right_locations[0] || ''));
        // stops per trip: where the box swaps fall (their walk to the station is counted)
        const load_sizes = data.loads.map(l => flattenLoad(l).length);
        // pieces per stop: the pace-based delta prices the picking still to do with them
        const qtys = data.loads.flatMap(l => flattenLoad(l).map(s => [...s.picker_left_items, ...s.picker_right_items].reduce((q, i) => q + (Number(i.quantity) || 0), 0)));
        track('list_shown', { orders: orderIds.length, stops, loads: data.loads.length,
                              unlocated: data.unlocated_items.length, mode: data.mode,
                              cart: data.cart || null, codes, load_sizes, qtys });
      })
      .catch(() => setLoading(false));
    return () => { flushTracking(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderIds]);

  // Details close when the screen moves on.
  useEffect(() => { setOpenKey(null); }, [phase, stopIdx, loadIdx]);

  // The live speed delta (Fernando, 2026-09-28): re-read after every stop and every 15 s.
  // `picker` is the person a device chose; on a personal login the server knows.
  useEffect(() => { if (phase === 'done') flushTracking(); }, [phase]);   // pick_done lands before the re-read
  const delta = useDelta({ roundId }, picker, `${phase}:${loadIdx}:${stopIdx}`);
  const [speedOpen, setSpeedOpen] = useState(false);
  const speedChip = <SpeedChip d={delta} theme="light" onClick={() => setSpeedOpen(true)} />;
  const speedSheet = speedOpen && delta ? <SpeedSheet d={delta} theme="light" onClose={() => setSpeedOpen(false)} /> : null;

  // What a stop looks like in the event log: where the picker stood and what was
  // there — locations and quantities only, never customer data.
  const stopPayload = (s: FlatStop | undefined, lIdx: number, sIdx: number) => s ? ({
    load: lIdx + 1, stop: sIdx + 1, aisle: s.aisle_name, box: s.box_label,
    left: s.picker_left_locations, right: s.picker_right_locations,
    items: s.picker_left_items.length + s.picker_right_items.length,
    qty: [...s.picker_left_items, ...s.picker_right_items].reduce((q, i) => q + i.quantity, 0),
  }) : {};

  const startLoad = (idx: number) => {
    if (!plan) return;
    const flat = flattenLoad(plan.loads[idx]);
    setLoadIdx(idx);
    setFlatStops(flat);
    setStopIdx(0);
    setPhase('picking');
    // Leaving grab-the-boxes (first load) or the box-swap screen (later loads).
    track(idx === 0 ? 'picking_started' : 'box_swap_continue',
          { load: idx + 1, boxes: plan.loads[idx].boxes, first_stop: stopPayload(flat[0], idx, 0),
            cart: plan.cart || null });
  };

  // A second tap within STEP_GUARD_MS of a stop change is ignored: round 308 (19.10) — two "Next" in the same second,
  // the G 14 stop went by unseen and its socks were never picked. Nobody picks a piece in under a second. -- Claude
  const stepAt = useRef(0);
  const stepGuard = () => {
    const now = Date.now();
    if (now - stepAt.current < STEP_GUARD_MS) return false;
    stepAt.current = now;
    return true;
  };

  const goNext = () => {
    if (!plan || !stepGuard()) return;
    track('pick_next', { from: stopPayload(flatStops[stopIdx], loadIdx, stopIdx), phase });
    if (stopIdx < flatStops.length - 1) {
      setStopIdx(s => s + 1);
    } else if (loadIdx < plan.loads.length - 1) {
      setSavedPos({ loadIdx, stopIdx });
      setPhase('box_change');
      track('box_swap', { after_load: loadIdx + 1, reason: 'last stop of load' });
    } else if (plan.unlocated_items.length > 0) {
      setPhase('unlocated');
    } else {
      setPhase('done');
      track('pick_done', { orders: orderIds.length, stops: totalStops, loads: plan.loads.length });
    }
  };

  const goPrev = () => {
    if (!plan || !stepGuard()) return;
    track('pick_prev', { from: stopPayload(flatStops[stopIdx], loadIdx, stopIdx), phase });
    if (phase === 'box_change') {
      if (savedPos) {
        if (savedPos.loadIdx !== loadIdx) {
          setLoadIdx(savedPos.loadIdx);
          setFlatStops(flattenLoad(plan.loads[savedPos.loadIdx]));
        }
        setStopIdx(savedPos.stopIdx);
      }
      setPhase('picking');
      return;
    }
    if (stopIdx > 0) {
      setStopIdx(s => s - 1);
    } else if (loadIdx > 0) {
      const prevIdx  = loadIdx - 1;
      const prevFlat = flattenLoad(plan.loads[prevIdx]);
      setLoadIdx(prevIdx);
      setFlatStops(prevFlat);
      setStopIdx(prevFlat.length - 1);
    } else {
      plan.skip_intro ? setPhase('summary') : setPhase('intro');
    }
  };

  const triggerBoxChange = () => {
    setSavedPos({ loadIdx, stopIdx });
    setPhase('box_change');
    track('box_swap', { after_load: loadIdx + 1, reason: 'button', at: stopPayload(flatStops[stopIdx], loadIdx, stopIdx) });
  };

  // Every other click on these screens, by name — Fernando wants each step
  // visible in the round timeline, not just Next/Prev.
  const btn = (name: string, extra?: Record<string, unknown>) => track('button', { name, phase, ...(extra || {}) });
  const closeTracked = () => { btn('close'); flushTracking(); exitFullscreen(); onClose(); };
  const toggleRow = (key: string) => {
    const opening = openKey !== key;
    setOpenKey(opening ? key : null);
    if (opening) btn('row_open');
  };

  // Keyboard navigation while picking: → = Next stop, ← = Prev stop.
  // Mirrors the on-screen Prev/Next buttons; ignored while typing in a field.
  useEffect(() => {
    if (phase !== 'picking') return;
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, goNext, goPrev]);

  const totalStops = plan
    ? plan.loads.reduce((s, l) => s + flattenLoad(l).length, 0)
    : 0;
  const stopsBeforeLoad = plan
    ? plan.loads.slice(0, loadIdx).reduce((s, l) => s + flattenLoad(l).length, 0)
    : 0;
  const globalStopNum = stopsBeforeLoad + stopIdx + 1;

  // Header shared by every phase: a left action, a title, an optional note on
  // the right, and the fullscreen toggle where the browser has one.
  const Header = ({ left, title, sub, right }: { left: React.ReactNode; title: string; sub?: string; right?: React.ReactNode }) => (
    <div className={`flex-none border-b ${C.header} px-4 sm:px-6 py-3 flex items-center justify-between gap-3`}>
      <div className="flex items-center gap-3 min-w-0">
        {left}
        <span className={`font-bold text-lg ${C.text} truncate`}>{title}</span>
        {sub && <span className={`text-sm ${C.muted} hidden sm:inline`}>{sub}</span>}
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {right}
        {onPause && <Btn onClick={() => { btn('pause'); flushTracking(); onPause(); }} className="px-3 py-2 text-sm">⏸ Pause</Btn>}
        {fsSupported && (
          <Btn onClick={toggleFullscreen} className="px-3 py-2 text-sm">
            {isFs ? '⛶ Exit full screen' : '⛶ Full screen'}
          </Btn>
        )}
      </div>
    </div>
  );
  const root = `fixed inset-0 ${C.bg} ${C.text} z-50 flex flex-col`;
  // "Just look": say so on every screen of the walk, so nobody mistakes the view for the round
  const viewBanner = viewOnly ? (
    <div className="flex-none bg-amber-500 text-black text-center text-sm font-bold py-1.5">
      👁 Viewing only — nothing is recorded; the round and its picker are not changed
    </div>
  ) : null;
  const cartChip = plan?.cart
    ? <span className={`text-xs px-2 py-1 rounded-full ${C.chip}`}>Cart: {plan.cart.name} · {plan.cart.boxes} {plan.cart.boxes === 1 ? 'box' : 'boxes'}</span>
    : null;

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={`fixed inset-0 ${C.bg} flex items-center justify-center z-50`}>
        <div className={`${C.text} text-xl`}>Building picking plan…</div>
      </div>
    );
  }

  if (!plan || plan.loads.length === 0) {
    return (
      <div className={`fixed inset-0 ${C.bg} flex items-center justify-center z-50`}>
        <div className="text-center space-y-4">
          <p className={`${C.text} text-xl`}>No items to pick</p>
          {plan && plan.unlocated_items.length > 0 && (
            <p className={`text-sm ${C.muted}`}>
              <span className={`font-black ${C.alert}`}>! </span>
              {plan.unlocated_items.length} item(s) have no warehouse location — pick manually.
            </p>
          )}
          <Btn onClick={() => { exitFullscreen(); onClose(); }} className="px-6 py-3">Close</Btn>
        </div>
      </div>
    );
  }

  const summaryRows  = buildSummaryRows(plan);
  const totalQty     = summaryRows.reduce((s, i) => s + i.quantity, 0);
  const locatedCount = summaryRows.filter(i => i.is_located).length;

  // ── Summary ───────────────────────────────────────────────────────────────────
  if (phase === 'summary') {
    return (
      <div className={root}>{viewBanner}
        <Header
          left={<Btn onClick={closeTracked} className="px-3 py-2 text-sm">✕ Close</Btn>}
          title="Picking list"
          sub={`${orderIds.length} orders · ${summaryRows.length} lines · ${totalQty} items`}
          right={<>
            {cartChip}
            {plan.unlocated_items.length > 0 && (
              <span className={`text-sm font-medium ${C.muted}`}>
                <span className={`font-black ${C.alert}`}>! </span>{plan.unlocated_items.length} unlocated at bottom
              </span>
            )}
          </>}
        />

        <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-4">
          <div className="max-w-4xl mx-auto">
            <RowList rows={summaryRows} openKey={openKey} onToggle={toggleRow}
              dividerBefore={(row, prev) =>
                !row.is_located && (prev === null || prev.is_located) && locatedCount > 0
                  ? 'No warehouse location — pick manually' : null} />
          </div>
        </div>

        <div className={`flex-none border-t ${C.header} px-4 sm:px-6 py-4`}>
          <Btn kind="primary"
            onClick={() => { btn('start_picking_from_list', { skip_intro: plan.skip_intro }); plan.skip_intro ? startLoad(0) : setPhase('intro'); }}
            className="w-full py-5 text-xl">
            Start picking →
          </Btn>
        </div>
      </div>
    );
  }

  // ── Intro: grab these boxes ───────────────────────────────────────────────────
  if (phase === 'intro') {
    const firstLoad  = plan.loads[0];
    const laterLoads = plan.loads.slice(1);
    return (
      <div className={root}>{viewBanner}
        <Header
          left={<Btn onClick={() => { btn('back_to_list'); setPhase('summary'); }} className="px-3 py-2 text-sm">← Back</Btn>}
          title="Picking"
          sub={`${totalStops} stops`}
          right={<>
            {cartChip}
            {plan.loads.length > 1 && <span className={`text-sm font-medium ${C.muted}`}>{plan.loads.length} trips</span>}
          </>}
        />

        <div className="flex-1 flex flex-col items-center justify-center px-8 gap-8 overflow-y-auto">
          <div className="text-center">
            <div className={`text-sm uppercase tracking-widest mb-2 ${C.muted}`}>Before you start</div>
            <div className={`text-2xl font-bold ${C.text}`}>Grab these boxes</div>
          </div>

          <BoxTiles boxes={firstLoad.boxes} />

          {laterLoads.length > 0 && (
            <div className={`text-sm text-center ${C.muted}`}>
              Later trips: {laterLoads.map(l => l.boxes.join(' + ')).join(' → ')}
            </div>
          )}

          <Btn kind="primary" onClick={() => startLoad(0)} className="px-16 py-5 text-xl">
            Start picking →
          </Btn>
        </div>
      </div>
    );
  }

  // ── Box change / trip complete ────────────────────────────────────────────────
  if (phase === 'box_change') {
    const nextLoadIdx = (savedPos?.loadIdx ?? loadIdx) + 1;
    const nextLoad    = plan.loads[nextLoadIdx];
    return (
      <div className={root}>{viewBanner}
        <Header
          left={<Btn onClick={() => { btn('back_from_box_swap'); goPrev(); }} className="px-3 py-2 text-sm">← Back</Btn>}
          title="Picking"
        />

        <div className="flex-1 flex flex-col items-center justify-center px-8 gap-8 overflow-y-auto">
          {nextLoad ? (
            <>
              <div className="text-center">
                <div className={`text-sm uppercase tracking-widest mb-2 ${C.muted}`}>Box swap</div>
                <div className={`text-2xl font-bold mb-1 ${C.text}`}>Swap to these boxes</div>
                <div className={`text-sm ${C.muted}`}>Trip {nextLoadIdx + 1} of {plan.loads.length}</div>
              </div>
              <BoxTiles boxes={nextLoad.boxes} />
              <Btn kind="primary" onClick={() => startLoad(nextLoadIdx)} className="px-16 py-5 text-xl">
                Continue →
              </Btn>
            </>
          ) : (
            <>
              <div className={`text-6xl ${C.text}`}>✓</div>
              <div className={`text-2xl font-bold ${C.text}`}>All trips done!</div>
              <Btn kind="primary"
                onClick={() => { track('pick_done', { orders: orderIds.length, stops: totalStops, loads: plan.loads.length, via: 'all trips done' }); setPhase('done'); }}
                className="px-16 py-5 text-xl">
                Complete
              </Btn>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Unlocated items ───────────────────────────────────────────────────────────
  if (phase === 'unlocated') {
    // Group by picklist+location so items with different temp locations stay separate
    const ulRows = sortRows(mergeRows(plan.unlocated_items.map(item => ({ item, side: 'none' as Side, located: false }))), []);

    return (
      <div className={root}>{viewBanner}
        <Header
          left={<Btn onClick={() => { btn('back_from_unlocated'); setPhase('picking'); }} className="px-3 py-2 text-sm">← Back</Btn>}
          title="Unlocated items"
          sub={`${ulRows.length} line${ulRows.length !== 1 ? 's' : ''} — not in the warehouse plan, pick manually`}
        />

        <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-4">
          <div className="max-w-4xl mx-auto">
            <RowList rows={ulRows} openKey={openKey} onToggle={toggleRow} />
          </div>
        </div>

        <div className={`flex-none border-t ${C.header} px-4 sm:px-6 py-4`}>
          <Btn kind="primary"
            onClick={() => { track('pick_done', { orders: orderIds.length, stops: totalStops, loads: plan.loads.length, unlocated: plan.unlocated_items.length, via: 'unlocated' }); setPhase('done'); }}
            className="w-full py-5 text-xl">
            Complete!
          </Btn>
        </div>
      </div>
    );
  }

  // ── Done ──────────────────────────────────────────────────────────────────────
  if (phase === 'done') {
    return (
      <div className={`${root} items-center justify-center gap-6`}>
        <div className={`text-7xl leading-none ${C.text}`}>✓</div>
        <div className={`text-2xl font-bold ${C.text}`}>Picking complete!</div>
        <div className={`text-sm ${C.muted}`}>{orderIds.length} orders · {totalStops} stops</div>
        {/* the picking recap: how the walk went, and where the round is heading if the packing
            goes like last time (the server has picking_done_at by now; the poll refreshes it) */}
        {delta && delta.round_id && (
          <div className={`rounded-2xl border px-6 py-4 text-center ${C.panel}`}>
            <div className={`text-sm ${C.muted}`}>Picking {delta.picking_min != null ? fmtDur(delta.picking_min) : '—'}
              {delta.sec_per_stop != null ? ` · ${delta.sec_per_stop} s/stop` : ''}
              {delta.ref ? ` (last round ${fmtDur(delta.ref.picking_min)} · ${delta.ref.sec_per_stop} s/stop)` : ''}</div>
            <div className="mt-2 flex items-baseline justify-center gap-2">
              <span className={`text-4xl font-black ${speedCls(delta.expected_oph, 'light')}`}>{delta.expected_oph != null ? delta.expected_oph.toFixed(1) : '—'}</span>
              <span className={`text-sm ${C.muted}`}>orders/h expected</span>
            </div>
            <div className={`text-sm font-semibold ${deltaCls(delta.delta_sign, 'light')}`}>
              {delta.ref && delta.delta != null
                ? `${deltaArrow(delta.delta_sign)} ${fmtDelta(delta.delta)} vs your last round (${delta.ref.oph.toFixed(1)}/h)`
                : 'no previous round to compare with'}
            </div>
          </div>
        )}
        <Btn kind="primary" onClick={() => { btn('done'); flushTracking(); exitFullscreen(); onClose(); }}
          className="px-10 py-4 text-lg mt-4">
          Done
        </Btn>
      </div>
    );
  }

  // ── Picking stop ──────────────────────────────────────────────────────────────
  const stop        = flatStops[stopIdx];
  const nextStop    = stopIdx < flatStops.length - 1 ? flatStops[stopIdx + 1] : null;
  const isVeryLast  = stopIdx === flatStops.length - 1 && loadIdx === plan.loads.length - 1;
  const showBoxSwap = plan.mode === 'single_trip' && loadIdx < plan.loads.length - 1;
  const rows        = stopRows(stop);

  return (
    <div className={root}>{viewBanner}
      <Header
        left={<Btn onClick={closeTracked} className="px-3 py-2 text-sm">✕ Close</Btn>}
        title={stop.aisle_name}
        right={
          <>
            <span className={`text-xs px-2 py-1 rounded-full ${C.chip}`}>
              {stop.box_label === 'P&P' ? '→ packing table' : `Box ${stop.box_label}`}</span>
            {plan.loads.length > 1 && (
              <span className={`text-xs px-2 py-1 rounded-full ${C.chip}`}>Trip {loadIdx + 1}/{plan.loads.length}</span>
            )}
            <span className={`text-sm font-medium ${C.muted}`}>{globalStopNum} / {totalStops}</span>
            {speedChip}
          </>
        }
      />
      {speedSheet}

      {/* Progress bar */}
      <div className={`flex-none h-1.5 ${C.bar}`}>
        <div className={`h-full ${C.barFill} transition-all duration-300`}
          style={{ width: `${(globalStopNum / totalStops) * 100}%` }} />
      </div>

      {/* The shelves at this stop, with the side they are on */}
      <div className={`flex-none flex items-center justify-center gap-6 sm:gap-10 px-6 py-4 border-b border-[#c8ccd2]`}>
        {stop.has_left && (
          <div className="flex items-center gap-3">
            <span className="text-4xl font-black leading-none" aria-hidden>←</span>
            {stop.picker_left_locations.map(loc => (
              <span key={loc} className="text-5xl font-black leading-none tracking-tight">{loc}</span>
            ))}
          </div>
        )}
        {stop.has_right && (
          <div className="flex items-center gap-3">
            {stop.picker_right_locations.map(loc => (
              <span key={loc} className="text-5xl font-black leading-none tracking-tight">{loc}</span>
            ))}
            <span className="text-4xl font-black leading-none" aria-hidden>→</span>
          </div>
        )}
      </div>

      {/* One row per item, arrows show the side */}
      <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 min-h-0">
        <div className="max-w-4xl mx-auto">
          <RowList rows={rows} openKey={openKey} onToggle={toggleRow} />
        </div>
      </div>

      {/* Next stop hint */}
      <div className={`flex-none px-6 py-2 border-t border-[#c8ccd2] flex items-center justify-center gap-4 min-h-[36px] text-sm ${C.muted}`}>
        {nextStop ? (
          <>
            <span className="text-xs uppercase tracking-wider flex-shrink-0">Next</span>
            {nextStop.aisle_name !== stop.aisle_name && (
              <span className="text-xs flex-shrink-0">[{nextStop.aisle_name}]</span>
            )}
            {nextStop.has_left && (
              <span className="font-bold">← {nextStop.picker_left_locations.join(', ')}</span>
            )}
            {nextStop.has_right && (
              <span className="font-bold">{nextStop.picker_right_locations.join(', ')} →</span>
            )}
          </>
        ) : (
          <span className="font-semibold">
            {loadIdx < plan.loads.length - 1
            ? 'Last stop — swap boxes next'
            : plan.unlocated_items.length > 0
              ? `Last stop — ${plan.unlocated_items.length} unlocated item(s) next`
              : 'Last stop — picking complete!'}
          </span>
        )}
      </div>

      {/* Navigation */}
      <div className={`flex-none border-t ${C.header} px-4 py-4 flex items-center gap-3`}>
        <Btn onClick={goPrev} className="flex-1 py-5 text-lg">← Prev</Btn>

        {showBoxSwap ? (
          <Btn onClick={triggerBoxChange} className="flex-1 py-5 text-sm">Box swap</Btn>
        ) : (
          <div className="flex-1" />
        )}

        <Btn kind="primary" onClick={goNext} className="flex-1 py-5 text-lg">
          {isVeryLast ? 'Complete!' : 'Next →'}
        </Btn>
      </div>
    </div>
  );
}
